// src/components/NotificationDropdown.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
	Dropdown,
	DropdownToggle,
	DropdownMenu,
	Nav,
	NavItem,
	NavLink,
	Badge,
	ListGroup,
	ListGroupItem,
	Button,
	Spinner,
	UncontrolledTooltip,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { formatDistanceToNow } from 'date-fns';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { Link } from 'react-router-dom';
import config from '../../config';
import { ApplicationState } from 'store';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { Action } from 'redux';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';

import {
	NotificationType,
	TabType,
	TABS,
	getMeta,
	render,
	getClickAction,
	CATEGORIES,
	CHANNELS
} from 'helpers/notifications/notificationConfig';
import {
	getNotifications,
	markAsRead,
	markAllAsRead,
	sseNewNotification,
} from 'store/notifications/action';
import { formatReadableDate } from 'helpers/date';

const { api: { DTS_API_NOTIFICATION } } = config;


type ChannelKey = string;
type TemplateKey = string;

type Mode = 'custom' | 'daily';
interface ReminderSetting {
	enabled: boolean;
	mode: Mode;
	offsets: number[];
	digestTime: string;
}

interface ChannelPermission {
	enabled: boolean;
	default: boolean;
}

type AdminNotificationSettingsMap = Record<
	TemplateKey,
	{
		channels: Record<ChannelKey, ChannelPermission>;
		reminder: ReminderSetting & { overrideable: boolean };
	}
>;

interface UserTemplateSetting {
	channels: Record<ChannelKey, boolean>;
	reminder: ReminderSetting;
}
type UserSettingsMap = Record<TemplateKey, UserTemplateSetting>;

export default function NotificationDropdown() {
	const navigate = useNavigate();
	const dispatch: ThunkDispatch<any, null, Action<string>> = useDispatch();
	const notifications = useSelector(
		(s: ApplicationState) => s.notifications.dropdownList
	)
	const loading = useSelector(
		(s: ApplicationState) => s.notifications.dropdownLoading
	);
	const isMarkAllLoading = useSelector(
		(s: ApplicationState) => s.notifications.isMarkAllLoading
	);
	const markingIds = useSelector(
		(s: ApplicationState) => s.notifications.markingIds
	);
	const token = useSelector((s: ApplicationState) => s.auth.token);
	const totalUnread = useSelector(
		(s: ApplicationState) => s.notifications.pagination.totalUnread
	);
	const unreadByType = useSelector(
		(s: ApplicationState) => s.notifications.pagination.unreadByType
	);

	// get the raw JSON blobs from Redux (or wherever you store them)
	const rawAdminJson = useSelector((s: ApplicationState) => s.auth?.userProfile?.tenant?.notificationSettings ?? '{}');
	const rawUserJson = useSelector((s: ApplicationState) => {
		const tId = s.auth?.userProfile?.tenant?.id;
		const ut = s.auth?.userProfile?.userTenants?.find((x: { tenant_id: string; }) => x.tenant_id === tId);
		return ut?.notificationSettings ?? '{}';
	});

	const [open, setOpen] = useState(false);
	const [activeTab, setActiveTab] = useState<TabType>('all');

	// memoize your parsed maps
	const adminMap = useMemo(() => mergeAdminRaw(rawAdminJson), [rawAdminJson]);
	const userSettings = useMemo(() => mergeUserRaw(rawUserJson, adminMap), [rawUserJson, adminMap]);

	// Fetch dropdown notifications once
	useEffect(() => {
		dispatch(getNotifications({ scope: 'dropdown', append: false, size: 1000 }));
	}, [dispatch]);

	// Subscribe to SSE
	useEffect(() => {
		const es = new EventSourcePolyfill(
			`${DTS_API_NOTIFICATION}/api/notifications/stream`,
			{ headers: { Authorization: `Bearer ${token}` } }
		);
		es.addEventListener('notification', (e: any) => {
			const parsed = JSON.parse(e.data);
			// 1) push into dropdown & counts
			dispatch(sseNewNotification(parsed));

			// 2) only toast if in-app is enabled for this template
			const tpl = parsed.template_key;
			if (userSettings[tpl]?.channels.inApp) {
				const { title } = render(tpl, parsed.payload || {});
				// show your toast (react-toastify assumed)
				toast.info(title, { position: 'top-right', autoClose: 5000 });
			}
		});
		return () => es.close();
	}, [dispatch, token]);

	const filtered = useMemo(() => {
		if (activeTab === 'all') return notifications;
		return notifications.filter(n => getMeta(n.template_key)?.type === activeTab);
	}, [notifications, activeTab]);


	// 1) Build admin defaults
	function buildAdminDefault(): AdminNotificationSettingsMap {
		const def = {} as AdminNotificationSettingsMap;
		CATEGORIES.forEach(cat =>
			cat.templates.forEach(tpl => {
				const channels: Record<ChannelKey, ChannelPermission> = {};
				CHANNELS.forEach(ch => {
					channels[ch.key] = { enabled: ch.defaultEnabled, default: ch.defaultEnabled };
				});
				def[tpl.key] = {
					channels,
					reminder: {
						enabled: false,
						overrideable: false,
						mode: 'custom',
						offsets: [],
						digestTime: '',
					},
				};
			})
		);
		return def;
	}

	// 2) Merge tenant JSON into admin defaults
	function mergeAdminRaw(rawJson: string): AdminNotificationSettingsMap {
		const merged = buildAdminDefault();
		let raw: any = {};
		try {
			raw = JSON.parse(rawJson);
		} catch { }
		Object.entries(raw).forEach(([tplKey, cfg]: any) => {
			if (!merged[tplKey]) return;
			if (cfg.channels) {
				Object.entries(cfg.channels).forEach(([chKey, perm]: any) => {
					if (merged[tplKey].channels[chKey]) {
						merged[tplKey].channels[chKey] = perm;
					}
				});
			}
			if (cfg.reminder) {
				const r = merged[tplKey].reminder;
				merged[tplKey].reminder = {
					enabled: cfg.reminder.enabled ?? r.enabled,
					overrideable: cfg.reminder.overrideable ?? r.overrideable,
					mode: cfg.reminder.mode ?? r.mode,
					offsets: cfg.reminder.offsets ?? r.offsets,
					digestTime: cfg.reminder.digestTime ?? r.digestTime,
				};
			}
		});
		return merged;
	}

	// 3) Merge user JSON on top of admin for initial state
	function mergeUserRaw(
		rawJson: string,
		adminMap: AdminNotificationSettingsMap
	): UserSettingsMap {
		const base = JSON.parse(rawJson || '{}');
		const merged = {} as UserSettingsMap;

		Object.entries(adminMap).forEach(([tplKey, adminCfg]) => {
			// Channels
			const userChs: Record<ChannelKey, boolean> = {};
			Object.entries(adminCfg.channels).forEach(([chKey, perm]) => {
				userChs[chKey] = perm.enabled
					? (base[tplKey]?.channels?.[chKey] ?? perm.default)
					: perm.default;
			});

			// Reminders
			const a = adminCfg.reminder;
			let rem: ReminderSetting = {
				enabled: a.enabled,
				mode: a.mode,
				offsets: a.offsets,
				digestTime: a.digestTime,
			};
			if (a.overrideable && base[tplKey]?.reminder) {
				const b: ReminderSetting = base[tplKey].reminder;
				rem = {
					enabled: b.enabled ?? rem.enabled,
					mode: b.mode ?? rem.mode,
					offsets: b.offsets ?? rem.offsets,
					digestTime: b.digestTime ?? rem.digestTime,
				};
			}
			if (!a.enabled) rem.enabled = false;

			merged[tplKey] = { channels: userChs, reminder: rem };
		});

		return merged;
	}

	return (
		<>
			<Dropdown isOpen={open} toggle={() => setOpen(prev => !prev)}>
				<DropdownToggle
					tag="button"
					className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle position-relative"
				>
					<i id="notificationDropdown" className="bx bx-bell fs-22" />
					{!loading && totalUnread > 0 && (
						<Badge
							color="danger"
							pill
							className="position-absolute top-0 end-0 ms-2"
							style={{ fontSize: '0.6rem' }}
						>
							{totalUnread}
						</Badge>
					)}
				</DropdownToggle>
				<UncontrolledTooltip placement="top" target="notificationDropdown">
					Notifications
				</UncontrolledTooltip>
				<DropdownMenu
					end
					style={{ width: 580, padding: 0, borderRadius: 12, overflow: 'hidden' }}
				>
					{/* Header */}
					<div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
						<h6 className="mb-0">Notifications</h6>
						<Button
							color="link"
							size="sm"
							className="text-primary d-flex align-items-center"
							onClick={() => dispatch(markAllAsRead('dropdown'))}
							disabled={isMarkAllLoading || totalUnread === 0}
						>
							<i className="ri-check-double-fill me-1" />
							<span>Mark all as read</span>
							{isMarkAllLoading && <Spinner size="sm" className="ms-2" />}
						</Button>
					</div>

					{/* Tabs */}
					<Nav
						pills
						className="nav-fill flex-nowrap p-1 bg-light"
					>
						{Object.entries(TABS).map(([key, { label, color }]) => {
							const isAll = key === 'all';
							const count = isAll ? totalUnread : unreadByType[key as NotificationType] || 0;
							return (
								<NavItem key={key} className="px-1">
									<NavLink
										className={`d-flex align-items-center cursor-pointer ${activeTab === key ? `shadow-sm text-${color}` : 'text-muted'
											}`}
										onClick={() => setActiveTab(key as NotificationType)}
										style={{ padding: '.375rem .75rem', whiteSpace: 'nowrap' }}
									>
										{label}
										<Badge pill className={`ms-1 ${activeTab === key ? '' : 'text-dark'}`} color={`${activeTab === key ? color : 'light'}`}>
											{count}
										</Badge>
									</NavLink>
								</NavItem>
							);
						})}
					</Nav>

					{/* List */}
					<ListGroup flush style={{ maxHeight: 300, overflowY: 'auto' }}>
						{filtered.length === 0 && !loading && (
							<div className="text-center p-4 text-muted">
								<i className="ri-notification-off-line fs-1 mb-2" />
								<p className="mb-0">You're all caught up!</p>
							</div>
						)}

						{filtered.map(n => {
							const { title, subtitle } = render(n.template_key, n.payload || {});
							const ago = formatDistanceToNow(
								new Date(n.sent_at ?? n.created_at),
								{ addSuffix: true }
							);
							const marking = markingIds.includes(n.id);
							const icon = getMeta(n.template_key)?.icon || 'ri-notification-line text-muted';
							const isDigest = n.payload?.items && Array.isArray(n.payload.items) && n.payload.items.length > 0;
							return (
								<ListGroupItem
									key={n.id}
									action
									disabled={marking}
									className={`d-flex cursor-pointer align-items-center px-3 py-2 ${marking ? 'opacity-50' : ''}`}
									onClick={() => {
										setOpen(false);
										if (n.read_status !== 'READ') {
											dispatch(markAsRead(n.id, 'dropdown'));
										}
										const url = getClickAction(n.template_key, n.payload || {});
										if (url.startsWith('http')) {
											window.open(url, '_blank');
										} else {
											navigate(url);
										}
									}}
								>
									{marking ? (
										<Spinner size="sm" className="me-3 text-primary" />
									) : (
										<div className=" me-3 p-2 lh-1 rounded-circle bg-light d-flex align-items-center justify-content-center">
											<i className={`${icon} fs-4`} />
										</div>
									)}

									<div className="flex-grow-1 me-3 overflow-hidden">
										<div className="fw-semibold text-truncate w-100">{title}</div>
										{isDigest ? (
											<ul className="text-muted small mb-0" style={{ paddingLeft: '1rem' }}>
												{n.payload.items.map((item: any, idx: number) => (
													<li key={idx}>
														{item.invitee_name || item.task_title}&nbsp;
														<small>— {formatReadableDate(item.start_time ?? item.due_date)}</small>
													</li>
												))}
											</ul>
										) : <div className="text-muted small text-truncate w-100 text-nowrap">{subtitle}</div>}
									</div>

									<div className="text-end flex-shrink-0">
										{n.read_status !== 'READ' && (
											<span
												className="d-inline-block rounded-circle"
												style={{ width: 10, height: 10, background: '#4A5BFE' }}
											/>
										)}
										<div>
											<small className="text-muted">{ago}</small>
										</div>
									</div>
								</ListGroupItem>
							);
						})}
					</ListGroup>

					{/* Footer */}
					<div className="text-center border-top py-2">
						<Link to="/notifications" className="text-primary fw-medium text-decoration-none">
							View all notifications
						</Link>
					</div>
				</DropdownMenu>
			</Dropdown>
		</>
	);
}
